import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Input, Select, Tabs, message, Spin, Alert, Tooltip, ConfigProvider, theme as antdTheme } from 'antd';
import { LoginOutlined, MailOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import {
  adminApi,
  type AdminUserSummary,
  type AdminUserDetail,
  type AdminActivityRecord,
} from '../api/admin';
import { startImpersonation } from '../admin/impersonation';
import {
  NotionTable,
  NotionPill,
  NotionPerson,
  NotionIcons,
  type NotionColumn,
  type NotionPillColor,
} from '../components/notion/NotionTable';
import { useNotionMessages } from '../components/notion/useNotionMessages';
import './Clients.css';
import './AdminUsers.css';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const DAY = 24 * 60 * 60 * 1000;
type UserStatus = 'active' | 'new' | 'inactive' | 'disabled';
type ViewKey = 'all' | 'active' | 'inactive' | 'noTelegram' | 'noLink' | 'emailed';
type TemplateKey = 'custom' | 'inactive' | 'noLink' | 'noTelegram' | 'unpaid';

const parseDate = (v: string | null | undefined): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const lastSeen = (u: AdminUserSummary): Date | null => {
  const a = parseDate(u.lastActivityAt);
  const b = parseDate(u.lastSignInAt);
  if (a && b) return a > b ? a : b;
  return a || b;
};

const statusOf = (u: AdminUserSummary): UserStatus => {
  if (u.disabled) return 'disabled';
  const seen = lastSeen(u);
  const created = parseDate(u.createdAt);
  const now = Date.now();
  if (seen && now - seen.getTime() <= 7 * DAY) return 'active';
  if (created && now - created.getTime() <= 7 * DAY) return 'new';
  if (!seen || now - seen.getTime() > 14 * DAY) return 'inactive';
  return 'active';
};

const cleanName = (s: string | null | undefined) => (s || '').replace(/\s+/g, ' ').trim();
const nameOf = (u: AdminUserSummary) => cleanName(u.displayName) || u.email || `#${u.id.slice(0, 6)}`;

const fill = (tpl: string, vars: Record<string, string>) =>
  Object.entries(vars).reduce((s, [k, v]) => s.split(`{${k}}`).join(v), tpl);

/** Follow-up email templates (English: the platform's end users are international). */
const TEMPLATES: Record<Exclude<TemplateKey, 'custom'>, { subject: string; body: string }> = {
  inactive: {
    subject: "We haven't seen you in a while 👋",
    body: `Hi {name},

It's been a few days since you last visited Telegram Stars. Your Telegram Stars payment links are still active and ready to collect payments.

Need a hand creating your next link or withdrawing your earnings? Just reply to this email.

Your dashboard: {appUrl}/app/dashboard

Talk soon,
The Telegram Stars team`,
  },
  noLink: {
    subject: 'Create your first payment link in 30 seconds ⭐',
    body: `Hi {name},

You created your Telegram Stars account but haven't made a payment link yet.

It takes about 30 seconds: pick a title and an amount in Stars, then share the link with your customers on Telegram. They pay in two taps and you are credited right away.

Create my first link: {appUrl}/app/invoices/new

If you have any question, just reply to this email.

The Telegram Stars team`,
  },
  noTelegram: {
    subject: 'Link your Telegram to receive your payments',
    body: `Hi {name},

Your Telegram Stars account isn't linked to Telegram yet. This is required to receive your payments in Stars and to be notified on every sale.

It takes 10 seconds: open the bot @{bot} and tap /start, or go to your settings: {appUrl}/app/settings

The Telegram Stars team`,
  },
  unpaid: {
    subject: 'Your payment links are still waiting 💤',
    body: `Hi {name},

You have payment links that haven't been paid yet. A quick reminder to your customers on Telegram often makes the difference: just send the link again in the conversation.

View my links: {appUrl}/app/invoices

Have a great day,
The Telegram Stars team`,
  },
};

/** Telegram follow-up templates (short: read in the bot chat, open buttons are added automatically). */
const TG_TEMPLATES: Record<Exclude<TemplateKey, 'custom'>, string> = {
  inactive: `Hey {name} 👋 It's been a while! Your Stars payment links are still active. Need a hand with your next sale? Reply to me here.`,
  noLink: `Hey {name} ⭐ You haven't created your first payment link yet. It takes 30 seconds: a title, an amount in Stars, and you share the link with your customers. Tap the button below 👇`,
  noTelegram: `Hey {name} 👋 Remember to finish setting up your account so you can receive your payments in Stars. Tap the button below to open your dashboard.`,
  unpaid: `Hey {name} 💤 You have pending payment links. A quick reminder to your customers on Telegram often makes the difference: just send the link again in the conversation!`,
};

/** Thème antd clair pour cette page : l'app est blanche, le thème global est sombre. */
const LIGHT_THEME = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: '#1a1a1a',
    colorInfo: '#1a1a1a',
    borderRadius: 8,
    colorText: '#37352f',
    colorTextSecondary: '#787774',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBorder: '#e3e2e0',
    colorBorderSecondary: '#ececea',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  components: {
    Input: { colorBgContainer: '#ffffff', colorBorder: '#e3e2e0', colorText: '#37352f', colorTextPlaceholder: '#9b9a97' },
    Button: { colorPrimary: '#1a1a1a', colorPrimaryHover: '#333333', colorPrimaryActive: '#000000' },
  },
};

/* ------------------------------------------------------------------ */
/* Composant                                                           */
/* ------------------------------------------------------------------ */

const AdminUsers: React.FC = () => (
  <ConfigProvider theme={LIGHT_THEME}>
    <AdminUsersInner />
  </ConfigProvider>
);

const AdminUsersInner: React.FC = () => {
  const { t } = useI18n();
  const { table: tt, adminUsers: ta, locale } = useNotionMessages();
  const navigate = useNavigate();
  const [msg, msgCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();

  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ViewKey>('all');

  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [emailTargets, setEmailTargets] = useState<AdminUserSummary[]>([]);
  const [template, setTemplate] = useState<TemplateKey>('custom');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [impersonating, setImpersonating] = useState<string | null>(null);

  const [tgTarget, setTgTarget] = useState<AdminUserSummary | null>(null);
  const [tgTemplate, setTgTemplate] = useState<TemplateKey>('custom');
  const [tgBody, setTgBody] = useState('');
  const [tgSending, setTgSending] = useState(false);

  /* ---------- Chargement ---------- */
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.listUsers(300);
      setUsers(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || t.app.common.error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    adminApi
      .getUser(detailId)
      .then((d) => !cancelled && setDetail(d))
      .catch((err) => !cancelled && msg.error(err?.response?.data?.message || err.message))
      .finally(() => !cancelled && setDetailLoading(false));
    return () => {
      cancelled = true;
    };
  }, [detailId, msg]);

  /* ---------- Dérivés ---------- */
  const formatDate = (d: Date) => d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  const formatDateTime = (d: Date) =>
    d.toLocaleString(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const relative = (d: Date | null): string => {
    if (!d) return ta.never;
    const diff = Date.now() - d.getTime();
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (diff < 60 * 1000) return rtf.format(0, 'minute');
    if (diff < 60 * 60 * 1000) return rtf.format(-Math.round(diff / 60000), 'minute');
    if (diff < DAY) return rtf.format(-Math.round(diff / 3600000), 'hour');
    if (diff < 30 * DAY) return rtf.format(-Math.round(diff / DAY), 'day');
    return formatDate(d);
  };
  const stars = (n: number) => (
    <span className="nt-num">
      {n.toLocaleString(locale)} <span style={{ color: '#9b9a97' }}>★</span>
    </span>
  );

  const statusMeta = (s: UserStatus): { label: string; color: NotionPillColor } => {
    switch (s) {
      case 'active': return { label: ta.statusActive, color: 'green' };
      case 'new': return { label: ta.statusNew, color: 'blue' };
      case 'disabled': return { label: ta.statusDisabled, color: 'red' };
      default: return { label: ta.statusInactive, color: 'gray' };
    }
  };

  const filtered = useMemo(() => {
    switch (activeView) {
      case 'active': return users.filter((u) => statusOf(u) === 'active');
      case 'inactive': return users.filter((u) => statusOf(u) === 'inactive');
      case 'noTelegram': return users.filter((u) => !u.telegramUserId);
      case 'noLink': return users.filter((u) => u.invoicesCount === 0);
      case 'emailed': return users.filter((u) => u.emailsSent > 0);
      default: return users;
    }
  }, [users, activeView]);

  const kpis = useMemo(
    () => ({
      users: users.length,
      active: users.filter((u) => statusOf(u) === 'active').length,
      inactive: users.filter((u) => statusOf(u) === 'inactive').length,
      revenue: users.reduce((s, u) => s + (u.revenueStars || 0), 0),
    }),
    [users],
  );

  const views = [
    { key: 'all', label: ta.viewAll, icon: NotionIcons.table, count: users.length },
    { key: 'active', label: ta.viewActive, icon: NotionIcons.person, count: users.filter((u) => statusOf(u) === 'active').length },
    { key: 'inactive', label: ta.viewInactive, icon: NotionIcons.date, count: users.filter((u) => statusOf(u) === 'inactive').length },
    { key: 'noTelegram', label: ta.viewNoTelegram, icon: NotionIcons.text, count: users.filter((u) => !u.telegramUserId).length },
    { key: 'noLink', label: ta.viewNoLink, icon: NotionIcons.url, count: users.filter((u) => u.invoicesCount === 0).length },
    { key: 'emailed', label: ta.viewEmailed, icon: NotionIcons.select, count: users.filter((u) => u.emailsSent > 0).length },
  ];

  /* ---------- Actions ---------- */
  const handleImpersonate = (u: AdminUserSummary) => {
    modal.confirm({
      title: ta.loginAsConfirmTitle,
      content: ta.loginAsConfirm,
      okText: ta.loginAs,
      cancelText: t.app.common.back,
      className: 'admin-users-confirm',
      onOk: async () => {
        try {
          setImpersonating(u.id);
          await startImpersonation(u.id);
          msg.success(fill(ta.loginAsStarted, { name: nameOf(u) }));
          setDetailId(null);
          navigate('/app/dashboard');
        } catch (err: any) {
          msg.error(err?.response?.data?.message || err.message || ta.loginAsError);
        } finally {
          setImpersonating(null);
        }
      },
    });
  };

  const defaultTemplateFor = (u: AdminUserSummary): TemplateKey => {
    if (!u.telegramUserId) return 'noTelegram';
    if (u.invoicesCount === 0) return 'noLink';
    if (u.invoicesCount > u.paidInvoicesCount && u.paidInvoicesCount === 0) return 'unpaid';
    if (statusOf(u) === 'inactive') return 'inactive';
    return 'custom';
  };

  const applyTemplate = (key: TemplateKey) => {
    setTemplate(key);
    if (key === 'custom') {
      setSubject('');
      setBody('Hi {name},\n\n');
      return;
    }
    const tpl = TEMPLATES[key];
    setSubject(tpl.subject);
    setBody(tpl.body);
  };

  const openEmail = (targets: AdminUserSummary[]) => {
    const withEmail = targets.filter((u) => u.email);
    if (!withEmail.length) {
      msg.warning(ta.emailNoAddress);
      return;
    }
    if (withEmail.length < targets.length) {
      msg.warning(ta.emailNoAddress);
    }
    setEmailTargets(withEmail);
    applyTemplate(withEmail.length === 1 ? defaultTemplateFor(withEmail[0]) : 'custom');
  };

  const insertTag = (tag: string) => {
    setBody((prev) => `${prev}${prev && !/\s$/.test(prev) ? ' ' : ''}${tag}`);
  };

  const sendEmail = async () => {
    if (!emailTargets.length) return;
    try {
      setSending(true);
      if (emailTargets.length === 1) {
        const res = await adminApi.sendEmail(emailTargets[0].id, { subject, message: body, template });
        msg.success(fill(ta.emailSent, { email: res.to }));
      } else {
        const res = await adminApi.sendBulkEmail({
          userIds: emailTargets.map((u) => u.id),
          subject,
          message: body,
          template,
        });
        if (res.failed.length) {
          msg.warning(fill(ta.emailSentPartial, { sent: String(res.sent), failed: String(res.failed.length) }));
        } else {
          msg.success(fill(ta.emailSentBulk, { sent: String(res.sent) }));
        }
      }
      const sentIds = new Set(emailTargets.map((u) => u.id));
      setSelectedIds((prev) => prev.filter((id) => !sentIds.has(id)));
      setEmailTargets([]);
      load();
      if (detailId && sentIds.has(detailId)) {
        adminApi.getUser(detailId).then(setDetail).catch(() => undefined);
      }
    } catch (err: any) {
      const m = err?.response?.data?.message || err.message || ta.emailError;
      msg.error(err?.response?.status === 503 ? ta.emailNotConfigured : m);
    } finally {
      setSending(false);
    }
  };

  /* ---------- Relance Telegram ---------- */
  const applyTgTemplate = (key: TemplateKey, u: AdminUserSummary) => {
    setTgTemplate(key);
    const name = cleanName(u.displayName);
    if (key === 'custom') {
      setTgBody(name ? `Hey ${name} 👋 ` : 'Hey 👋 ');
      return;
    }
    setTgBody(fill(TG_TEMPLATES[key], { name }).replace('Hey  ', 'Hey '));
  };

  const openTelegram = (u: AdminUserSummary) => {
    if (!u.telegramUserId) {
      msg.warning(ta.telegramNotLinked);
      return;
    }
    setTgTarget(u);
    const def = defaultTemplateFor(u);
    applyTgTemplate(def === 'noTelegram' ? 'custom' : def, u);
  };

  const sendTelegram = async () => {
    if (!tgTarget) return;
    try {
      setTgSending(true);
      await adminApi.sendTelegram(tgTarget.id, { message: tgBody, template: tgTemplate });
      msg.success(ta.telegramSent);
      setTgTarget(null);
      load();
      if (detailId === tgTarget.id) {
        adminApi.getUser(tgTarget.id).then(setDetail).catch(() => undefined);
      }
    } catch (err: any) {
      msg.error(err?.response?.data?.message || err.message || ta.telegramError);
    } finally {
      setTgSending(false);
    }
  };

  /* ---------- Colonnes ---------- */
  const columns: NotionColumn<AdminUserSummary>[] = useMemo(
    () => [
      {
        key: 'name',
        label: ta.colUser,
        type: 'title',
        width: 'minmax(240px, 1.5fr)',
        accessor: (u) => `${u.displayName || ''} ${u.email || ''}`.trim(),
        render: (u) => (
          <NotionPerson
            name={nameOf(u)}
            sub={u.displayName && u.email ? u.email : u.email ? undefined : ta.noEmail}
          />
        ),
      },
      {
        key: 'status',
        label: ta.colStatus,
        type: 'select',
        width: 120,
        accessor: (u) => statusMeta(statusOf(u)).label,
        render: (u) => {
          const m = statusMeta(statusOf(u));
          return <NotionPill color={m.color}>{m.label}</NotionPill>;
        },
      },
      {
        key: 'telegram',
        label: ta.colTelegram,
        type: 'checkbox',
        width: 120,
        accessor: (u) => (u.telegramUserId ? ta.linked : ta.notLinked),
        render: (u) =>
          u.telegramUserId ? (
            <Tooltip title={u.telegramUserId}>
              <span><NotionPill color="blue">{ta.linked}</NotionPill></span>
            </Tooltip>
          ) : (
            <NotionPill color="default">{ta.notLinked}</NotionPill>
          ),
      },
      {
        key: 'links',
        label: ta.colLinks,
        type: 'number',
        width: 110,
        align: 'right',
        accessor: (u) => u.invoicesCount,
        render: (u) => (
          <span className="nt-num">
            {u.paidInvoicesCount}<span style={{ color: '#9b9a97' }}>/{u.invoicesCount}</span>
          </span>
        ),
      },
      {
        key: 'revenue',
        label: ta.colRevenue,
        type: 'number',
        width: 140,
        align: 'right',
        accessor: (u) => u.revenueStars,
        render: (u) => stars(u.revenueStars),
      },
      {
        key: 'lastActivity',
        label: ta.colLastActivity,
        type: 'date',
        width: 150,
        accessor: (u) => parseDate(u.lastActivityAt) || new Date(0),
        render: (u) => <span className={parseDate(u.lastActivityAt) ? '' : 'admin-users-muted'}>{relative(parseDate(u.lastActivityAt))}</span>,
      },
      {
        key: 'lastSignIn',
        label: ta.colLastSignIn,
        type: 'date',
        width: 150,
        accessor: (u) => parseDate(u.lastSignInAt) || new Date(0),
        render: (u) => <span className={parseDate(u.lastSignInAt) ? '' : 'admin-users-muted'}>{relative(parseDate(u.lastSignInAt))}</span>,
      },
      {
        key: 'emails',
        label: ta.colEmails,
        type: 'number',
        width: 110,
        align: 'right',
        accessor: (u) => u.emailsSent,
        render: (u) =>
          u.emailsSent ? (
            <Tooltip title={parseDate(u.lastEmailAt) ? formatDateTime(parseDate(u.lastEmailAt)!) : ''}>
              <span className="nt-num">{u.emailsSent}</span>
            </Tooltip>
          ) : (
            <span className="nt-empty-cell" />
          ),
      },
      {
        key: 'createdAt',
        label: ta.colSignedUp,
        type: 'date',
        width: 140,
        accessor: (u) => parseDate(u.createdAt) || new Date(0),
        render: (u) => (parseDate(u.createdAt) ? formatDate(parseDate(u.createdAt)!) : <span className="nt-empty-cell" />),
      },
      {
        key: 'actions',
        label: '',
        type: 'text',
        width: 128,
        sortable: false,
        accessor: () => '',
        render: (u) => (
          <span className="admin-users-actions" onClick={(e) => e.stopPropagation()}>
            <Tooltip title={ta.loginAs}>
              <button
                type="button"
                className="admin-users-iconbtn"
                disabled={impersonating === u.id}
                onClick={() => handleImpersonate(u)}
              >
                <LoginOutlined />
              </button>
            </Tooltip>
            <Tooltip title={u.telegramUserId ? ta.sendTelegram : ta.telegramNotLinked}>
              <button
                type="button"
                className="admin-users-iconbtn"
                disabled={!u.telegramUserId}
                onClick={() => openTelegram(u)}
              >
                <SendOutlined />
              </button>
            </Tooltip>
            <Tooltip title={u.email ? ta.sendEmail : ta.noEmail}>
              <button
                type="button"
                className="admin-users-iconbtn"
                disabled={!u.email}
                onClick={() => openEmail([u])}
              >
                <MailOutlined />
              </button>
            </Tooltip>
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ta, locale, impersonating],
  );

  const labels = {
    search: tt.search,
    sort: tt.sort,
    filter: tt.filter,
    open: tt.open,
    count: tt.count,
    empty: tt.empty,
    clearSort: tt.clearSort,
    ascending: tt.ascending,
    descending: tt.descending,
  };

  /* ---------- Rendu : activité ---------- */
  const renderActivity = (a: AdminActivityRecord) => {
    const at = parseDate(a.at);
    let label = a.action;
    let color: NotionPillColor = 'default';
    if (a.kind === 'page') {
      label = a.meta?.path || a.action.replace(/^page /, '');
      color = 'gray';
    } else if (a.kind === 'api') {
      color = (a.meta?.statusCode || 200) >= 400 ? 'red' : 'blue';
    } else if (a.kind === 'email') {
      label = `✉️ ${a.meta?.subject || a.action}`;
      color = 'orange';
    } else if (a.kind === 'telegram') {
      label = `✈️ ${a.meta?.preview || a.action}`;
      color = 'blue';
    } else if (a.kind === 'admin') {
      color = 'purple';
    }
    const detailText =
      a.kind === 'api'
        ? [a.meta?.body?.title, a.meta?.body?.amount && `${a.meta.body.amount} ★`, a.meta?.statusCode >= 400 && a.meta?.error]
            .filter(Boolean)
            .join(' · ')
        : '';
    return (
      <div key={a.id} className="admin-users-event">
        <div className="admin-users-event-time">{at ? formatDateTime(at) : ''}</div>
        <div className="admin-users-event-body">
          <NotionPill color={color}>{a.kind}</NotionPill>
          <span className="admin-users-event-label">{label}</span>
          {detailText && <span className="admin-users-event-detail">{detailText}</span>}
          {a.impersonatedBy && <span className="admin-users-event-admin">{ta.byAdmin}</span>}
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="clients-page">
        <Alert type="error" showIcon message={t.app.common.error} description={error} />
      </div>
    );
  }

  const du = detail?.user;
  const duStatus = du ? statusMeta(statusOf(du)) : null;

  return (
    <div className="clients-page admin-users-page">
      {msgCtx}
      {modalCtx}

      {/* KPIs */}
      <div className="clients-kpis">
        <div className="clients-kpi" style={{ ['--i' as string]: 0 }}>
          <span className="clients-kpi-label">{ta.kpiUsers}</span>
          <span className="clients-kpi-value">{loading ? '—' : kpis.users}</span>
        </div>
        <div className="clients-kpi" style={{ ['--i' as string]: 1 }}>
          <span className="clients-kpi-label">{ta.kpiActive7d}</span>
          <span className="clients-kpi-value">{loading ? '—' : kpis.active}</span>
        </div>
        <div className="clients-kpi" style={{ ['--i' as string]: 2 }}>
          <span className="clients-kpi-label">{ta.kpiInactive}</span>
          <span className="clients-kpi-value">{loading ? '—' : kpis.inactive}</span>
        </div>
        <div className="clients-kpi" style={{ ['--i' as string]: 3 }}>
          <span className="clients-kpi-label">{ta.kpiRevenue}</span>
          <span className="clients-kpi-value">{loading ? '—' : <>{kpis.revenue.toLocaleString(locale)} <em>★</em></>}</span>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="admin-users-bulkbar" role="toolbar">
          <span>{fill(ta.emailSelected, { count: String(selectedIds.length) })}</span>
          <button
            type="button"
            className="admin-users-bulkbar-send"
            onClick={() => openEmail(users.filter((u) => selectedIds.includes(u.id)))}
          >
            <MailOutlined /> {ta.emailSelectSend}
          </button>
          <button type="button" className="admin-users-bulkbar-clear" onClick={() => setSelectedIds([])}>
            {t.app.common.cancel}
          </button>
        </div>
      )}

      <NotionTable<AdminUserSummary>
        columns={columns}
        rows={filtered}
        rowKey={(u) => u.id}
        views={views}
        activeView={activeView}
        onViewChange={(k) => setActiveView(k as ViewKey)}
        onOpen={(u) => setDetailId(u.id)}
        defaultSort={{ key: 'lastActivity', dir: 'desc' }}
        loading={loading}
        formatDate={formatDate}
        labels={labels}
        selectable
        selectedKeys={selectedIds}
        onSelectedKeysChange={setSelectedIds}
        toolbarExtra={
          <>
            {selectedIds.length > 0 && (
              <button
                type="button"
                className="nt-inline-btn"
                onClick={() => openEmail(users.filter((u) => selectedIds.includes(u.id)))}
              >
                <MailOutlined /> {ta.emailSelectSend}
                <span className="admin-users-selcount">{selectedIds.length}</span>
              </button>
            )}
            <button type="button" className="nt-inline-btn" onClick={load} title={ta.refresh}>
              <ReloadOutlined /> {ta.refresh}
            </button>
          </>
        }
      />

      {/* Fiche utilisateur */}
      <Modal
        open={!!detailId}
        onCancel={() => setDetailId(null)}
        footer={null}
        width={760}
        destroyOnClose
        className="nt-detail-modal admin-users-modal"
      >
        {detailLoading && !detail && (
          <div className="admin-users-loading"><Spin /></div>
        )}
        {du && duStatus && (
          <div className="nt-detail">
            <div className="nt-detail-title admin-users-detail-head">
              <NotionPerson name={nameOf(du)} sub={du.email || ta.noEmail} />
              <div className="admin-users-detail-actions">
                <Button icon={<LoginOutlined />} onClick={() => handleImpersonate(du)} loading={impersonating === du.id}>
                  {ta.loginAs}
                </Button>
                <Button
                  type={du.telegramUserId ? 'primary' : 'default'}
                  icon={<SendOutlined />}
                  disabled={!du.telegramUserId}
                  onClick={() => openTelegram(du)}
                >
                  {ta.sendTelegram}
                </Button>
                <Button
                  type={du.telegramUserId ? 'default' : 'primary'}
                  icon={<MailOutlined />}
                  disabled={!du.email}
                  onClick={() => openEmail([du])}
                >
                  {ta.sendEmail}
                </Button>
              </div>
            </div>

            {!detail.mailConfigured && (
              <Alert type="warning" showIcon message={ta.emailNotConfigured} style={{ marginBottom: 12 }} />
            )}

            <div className="nt-props">
              <div className="nt-prop-label">{NotionIcons.select} {ta.colStatus}</div>
              <div className="nt-prop-value"><NotionPill color={duStatus.color}>{duStatus.label}</NotionPill></div>

              <div className="nt-prop-label">{NotionIcons.text} {ta.userId}</div>
              <div className="nt-prop-value"><code className="admin-users-code">{du.id}</code></div>

              <div className="nt-prop-label">{NotionIcons.checkbox} {ta.colTelegram}</div>
              <div className="nt-prop-value">
                {du.telegramUserId ? <><NotionPill color="blue">{ta.linked}</NotionPill> <span className="admin-users-muted">#{du.telegramUserId}</span></> : <NotionPill color="default">{ta.notLinked}</NotionPill>}
              </div>

              <div className="nt-prop-label">{NotionIcons.number} {ta.colLinks}</div>
              <div className="nt-prop-value">{du.paidInvoicesCount} / {du.invoicesCount}</div>

              <div className="nt-prop-label">{NotionIcons.number} {ta.colRevenue}</div>
              <div className="nt-prop-value">{stars(du.revenueStars)}</div>

              <div className="nt-prop-label">{NotionIcons.date} {ta.colLastSignIn}</div>
              <div className="nt-prop-value">{relative(parseDate(du.lastSignInAt))}</div>

              <div className="nt-prop-label">{NotionIcons.date} {ta.colLastActivity}</div>
              <div className="nt-prop-value">{relative(parseDate(du.lastActivityAt))}</div>

              <div className="nt-prop-label">{NotionIcons.date} {ta.colSignedUp}</div>
              <div className="nt-prop-value">{parseDate(du.createdAt) ? formatDate(parseDate(du.createdAt)!) : '—'}</div>

              <div className="nt-prop-label">{NotionIcons.url} {ta.tonAddress}</div>
              <div className="nt-prop-value">{du.tonAddress ? <code className="admin-users-code">{du.tonAddress}</code> : <span className="admin-users-muted">—</span>}</div>
            </div>

            <div className="nt-detail-sep" />

            <Tabs
              className="admin-users-tabs"
              items={[
                {
                  key: 'activity',
                  label: `${ta.tabActivity} (${detail.activity.length})`,
                  children: detail.activity.length ? (
                    <div className="admin-users-timeline">{detail.activity.map(renderActivity)}</div>
                  ) : (
                    <div className="admin-users-empty">{ta.activityEmpty}</div>
                  ),
                },
                {
                  key: 'invoices',
                  label: `${ta.tabInvoices} (${detail.invoices.length})`,
                  children: detail.invoices.length ? (
                    <div className="nt-detail-list">
                      {detail.invoices.map((inv) => {
                        const created = parseDate(inv.createdAt);
                        const color: NotionPillColor = inv.status === 'PAID' ? 'green' : inv.status === 'EXPIRED' ? 'red' : 'yellow';
                        return (
                          <div key={inv.id} className="nt-detail-item">
                            <div>
                              <div>{inv.title || inv.description || t.app.common.noTitle}</div>
                              <div className="sub">{created ? formatDate(created) : ''}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <NotionPill color={color}>{inv.status}</NotionPill>
                              {Number.isFinite(Number(inv.starsAmount ?? inv.amountStars)) && stars(Number(inv.starsAmount ?? inv.amountStars))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="admin-users-empty">{ta.invoicesEmpty}</div>
                  ),
                },
                {
                  key: 'payments',
                  label: `${ta.tabPayments} (${detail.payments.length})`,
                  children: detail.payments.length ? (
                    <div className="nt-detail-list">
                      {detail.payments.map((p) => {
                        const created = parseDate(p.createdAt);
                        return (
                          <div key={p.id} className="nt-detail-item">
                            <div>
                              <div>{p.payerName || (p.payerUsername ? `@${p.payerUsername}` : p.invoiceId || p.id)}</div>
                              <div className="sub">{created ? formatDateTime(created) : ''}</div>
                            </div>
                            {stars(p.amountStars || 0)}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="admin-users-empty">{ta.paymentsEmpty}</div>
                  ),
                },
                {
                  key: 'emails',
                  label: `${ta.tabEmails} (${detail.emails.length})`,
                  children: detail.emails.length ? (
                    <div className="nt-detail-list">
                      {detail.emails.map((e) => {
                        const at = parseDate(e.sentAt);
                        return (
                          <div key={e.id} className="nt-detail-item admin-users-email-item">
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <NotionPill color={e.channel === 'telegram' ? 'blue' : 'orange'}>
                                  {e.channel === 'telegram' ? ta.channelTelegram : ta.channelEmail}
                                </NotionPill>
                                <span>{e.subject || e.message.split('\n')[0].slice(0, 60)}</span>
                              </div>
                              <div className="sub">{at ? formatDateTime(at) : ''} · {e.channel === 'telegram' ? `#${e.to}` : e.to}</div>
                              <div className="admin-users-email-preview">{e.message}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="admin-users-empty">{ta.emailsEmpty}</div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Modal>

      {/* Modale e-mail */}
      <Modal
        open={emailTargets.length > 0}
        onCancel={() => setEmailTargets([])}
        title={
          emailTargets.length === 1
            ? fill(ta.emailTitle, { name: nameOf(emailTargets[0]) })
            : fill(ta.emailTitleBulk, { count: String(emailTargets.length) })
        }
        okText={ta.emailSend}
        cancelText={t.app.common.back}
        onOk={sendEmail}
        confirmLoading={sending}
        okButtonProps={{ disabled: subject.trim().length < 2 || body.trim().length < 5 }}
        width={640}
        destroyOnClose
        className="nt-detail-modal admin-users-email-modal"
      >
        {emailTargets.length > 0 && (
          <div className="admin-users-email-form">
            <div className="admin-users-email-to">
              <MailOutlined />{' '}
              {emailTargets.length === 1
                ? emailTargets[0].email
                : emailTargets.map((u) => u.email).join(', ')}
            </div>
            <label className="admin-users-field">
              <span>{ta.emailTemplate}</span>
              <Select<TemplateKey>
                value={template}
                onChange={(k) => applyTemplate(k)}
                options={[
                  { value: 'custom', label: ta.templateCustom },
                  { value: 'inactive', label: ta.templateInactive },
                  { value: 'noLink', label: ta.templateNoLink },
                  { value: 'noTelegram', label: ta.templateNoTelegram },
                  { value: 'unpaid', label: ta.templateUnpaid },
                ]}
              />
            </label>
            <label className="admin-users-field">
              <span>{ta.emailSubject}</span>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
            </label>
            <label className="admin-users-field">
              <span>{ta.emailMessage}</span>
              <Input.TextArea value={body} onChange={(e) => setBody(e.target.value)} autoSize={{ minRows: 8, maxRows: 18 }} maxLength={5000} />
            </label>
            <div className="admin-users-tags">
              <span>{ta.emailTagsLabel}</span>
              {['{name}', '{email}', '{appUrl}', '{bot}'].map((tag) => (
                <button key={tag} type="button" className="admin-users-tag" onClick={() => insertTag(tag)}>
                  {tag}
                </button>
              ))}
            </div>
            <div className="admin-users-hint">{ta.emailTagsHint}</div>
          </div>
        )}
      </Modal>

      {/* Modale Telegram */}
      <Modal
        open={!!tgTarget}
        onCancel={() => setTgTarget(null)}
        title={tgTarget ? fill(ta.telegramTitle, { name: nameOf(tgTarget) }) : ''}
        okText={ta.emailSend}
        cancelText={t.app.common.back}
        onOk={sendTelegram}
        confirmLoading={tgSending}
        okButtonProps={{ disabled: tgBody.trim().length < 5, icon: <SendOutlined /> }}
        width={560}
        destroyOnClose
        className="nt-detail-modal admin-users-email-modal"
      >
        {tgTarget && (
          <div className="admin-users-email-form">
            <div className="admin-users-email-to">
              <SendOutlined /> Telegram #{tgTarget.telegramUserId}
            </div>
            <label className="admin-users-field">
              <span>{ta.emailTemplate}</span>
              <Select<TemplateKey>
                value={tgTemplate}
                onChange={(k) => applyTgTemplate(k, tgTarget)}
                options={[
                  { value: 'custom', label: ta.templateCustom },
                  { value: 'inactive', label: ta.templateInactive },
                  { value: 'noLink', label: ta.templateNoLink },
                  { value: 'unpaid', label: ta.templateUnpaid },
                ]}
              />
            </label>
            <label className="admin-users-field">
              <span>{ta.emailMessage}</span>
              <Input.TextArea value={tgBody} onChange={(e) => setTgBody(e.target.value)} autoSize={{ minRows: 5, maxRows: 14 }} maxLength={3500} showCount />
            </label>
            <div className="admin-users-hint">{ta.telegramHint}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminUsers;
